// src/pages/Menu.js
// Главная страница витрины товаров с категориями, поиском и корзиной
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../CartContext';
import ProductCard from '../components/ProductCard';
import CategoryTabs from '../components/CategoryTabs';
import StickyCart from '../components/StickyCart';
import ProductModal from '../components/ProductModal';
import { Search, X, Frown } from 'lucide-react';
import { triggerHaptic } from '../utils/haptics';
import { useText } from '../utils/locales';

/**
 * Компонент скелетона для плавной загрузки витрины
 */
const MenuSkeleton = () => (
  <div className="min-h-screen bg-tg-bg pb-32 animate-pulse">
    {/* Скелетон шапки категорий */}
    <div className="sticky top-0 z-20 bg-tg-bg/95 backdrop-blur-md pb-3 pt-tg px-4 border-b border-gray-100/5">
      <div className="h-9 bg-tg-secondary-bg rounded-xl mb-3" />
      <div className="flex gap-2 overflow-x-hidden">
        {[80, 110, 95, 120].map((width, idx) => (
          <div key={idx} className="h-8 bg-tg-secondary-bg rounded-xl shrink-0" style={{ width }} />
        ))}
      </div>
    </div>

    {/* Скелетон сетки товаров */}
    <div className="px-4 pt-6 space-y-8">
      <div>
        <div className="h-6 w-32 bg-tg-secondary-bg rounded-lg mb-4" />
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-tg-secondary-bg rounded-2xl p-2.5 flex flex-col gap-2">
              <div className="aspect-square w-full bg-tg-bg rounded-xl" />
              <div className="h-4 bg-tg-bg rounded w-3/4 mt-1" />
              <div className="h-3 bg-tg-bg rounded w-1/2" />
              <div className="flex justify-between items-center mt-2">
                <div className="h-5 bg-tg-bg rounded w-12" />
                <div className="w-8 h-8 rounded-full bg-tg-bg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Menu = () => {
  const [menuData, setMenuData] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const t = useText();
  
  const isClickScrolling = useRef(false);
  const scrollTimeoutRef = useRef(null);
  
  const { cartItems, addToCart, removeFromCart, getCartTotal } = useContext(CartContext);

  const quantityMap = useMemo(() => {
    return cartItems.reduce((acc, item) => {
      acc[item.id] = (acc[item.id] || 0) + item.quantity;
      return acc;
    }, {});
  }, [cartItems]);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get('/api/menu');
        setMenuData(response.data);
        if (response.data.length > 0) setActiveCategory(response.data[0].id);
      } catch (error) {
        console.error("Menu fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMenu();
  }, []);

  // Наблюдатель IntersectionObserver для переключения активной категории при скролле
  useEffect(() => {
    if (isLoading || menuData.length === 0 || searchQuery.trim()) return;

    const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -70% 0px', 
        threshold: 0
    };

    const observerCallback = (entries) => {
        if (isClickScrolling.current) return;

        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const id = Number(entry.target.id.replace('category-', ''));
                setActiveCategory(id);
            }
        });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    menuData.forEach((cat) => {
        const element = document.getElementById(`category-${cat.id}`);
        if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [isLoading, menuData, searchQuery]);

  const handleAddButtonClick = (item) => {
    if (item.modifiers && item.modifiers.length > 0) {
        setSelectedItem(item);
    } else {
        addToCart(item);
        triggerHaptic('light');
    }
  };

  const scrollToCategory = (catId) => {
    triggerHaptic('selection');
    setActiveCategory(catId);
    
    isClickScrolling.current = true;
    const element = document.getElementById(`category-${catId}`);
    
    if (element) {
      const headerOffset = 150; 
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        isClickScrolling.current = false;
      }, 800); 
    }
  };

  // Фильтрация товаров по поисковому запросу
  const displayedCategories = useMemo(() => {
    if (!searchQuery.trim()) return menuData;
    const q = searchQuery.toLowerCase().trim();
    return menuData.map(cat => ({
      ...cat,
      items: cat.items.filter(item => 
        (item.name && item.name.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q)) ||
        (item.tags && String(item.tags).toLowerCase().includes(q))
      )
    })).filter(cat => cat.items.length > 0);
  }, [menuData, searchQuery]);

  if (isLoading) {
    return <MenuSkeleton />;
  }

  const categoriesList = menuData.map(cat => ({ id: cat.id, name: cat.name }));
  const totalCartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <div className="min-h-screen bg-tg-bg pb-40 transition-colors duration-300">
      
      {/* Шапка с поиском и табами категорий */}
      <div className="sticky top-0 z-20 bg-tg-bg/95 backdrop-blur-md shadow-sm pb-2 pt-tg border-b border-gray-100/5">
        {/* Строка поиска */}
        <div className="px-4 mb-2">
          <div className="bg-tg-secondary-bg rounded-xl px-3 py-2 flex items-center gap-2 border border-gray-100/5">
            <Search size={16} className="text-tg-hint shrink-0" />
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Найти в меню..."
              className="bg-transparent text-sm w-full outline-none text-tg-text placeholder:text-tg-hint placeholder:text-xs"
            />
            {searchQuery && (
              <button 
                type="button" 
                onClick={() => setSearchQuery('')} 
                className="text-tg-hint p-0.5 active:opacity-60"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Табы категорий (показываем, если не включен глобальный поиск) */}
        {!searchQuery.trim() && (
          <CategoryTabs 
            categories={categoriesList} 
            activeCategory={activeCategory} 
            onSelect={scrollToCategory} 
          />
        )}
      </div>

      {/* Список товаров */}
      <div className="px-4 pt-4 space-y-8">
        {displayedCategories.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center text-tg-hint text-center">
            <Frown size={48} className="mb-3 opacity-40" />
            <p className="font-bold text-base text-tg-text">Ничего не найдено</p>
            <p className="text-xs mt-1">Попробуйте изменить поисковый запрос</p>
          </div>
        ) : (
          displayedCategories.map((cat) => (
            <div 
              key={cat.id} 
              id={`category-${cat.id}`} 
              className="scroll-mt-[150px]"
            >
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-xl font-black text-tg-text tracking-tight uppercase">
                  {cat.name}
                </h2>
                <div className="h-px flex-1 bg-tg-text/10" />
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-4">
                {cat.items.map((item) => (
                  <ProductCard 
                    key={item.id}
                    item={item} 
                    quantity={quantityMap[item.id] || 0}
                    onAdd={() => handleAddButtonClick(item)}
                    onRemove={() => removeFromCart(item.id)}
                    onClick={() => setSelectedItem(item)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <StickyCart 
        totalAmount={getCartTotal()} 
        totalCount={totalCartCount}
        onClick={() => navigate('/cart')} 
      />

      <ProductModal 
        product={selectedItem}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </div>
  );
};

export default Menu;
